import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  Grid
} from "@mui/material";
import { Add, Delete, DeleteOutline, Download, FilterList, MoreVert, Search, FirstPage, LastPage, Close, PhotoCamera, Remove } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { TrashOutline } from "react-ionicons";
import "../../../shared/components/Item.css";
import { useNavigate } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";


import { getItems, createItemDetail, uploadItemDetailPhoto, editItemDetail, fetchItemPhotoBlob, getItemDetails, deleteItemDetail, ItemDetailSearchParams } from "../services/itemDetailsService.tsx";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';

// Set z-index global
Swal.mixin({
  customClass: {
    popup: 'swal-z-index'
  },
  backdrop: `rgba(0,0,0,0.4)`
});

// Dummy Data
const initialRows = Array.from({ length: 50 }, (_, index) => ({
  id: index,
  boldText: "Bold text column",
  textColumn1: "Regular text column",
  textColumn2: "Regular text column",
  textColumn3: "Regular text column",
  status: index % 3 === 0 ? "Inactive" : "Active",
  textColumn4: "Regular text column",
}));


const ItemDetails: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // Total semua data
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddCategoryDialog, setOpenAddCategoryDialog] = useState(false);
  const [openAddItemDetailsDialog, setOpenAddItemDetailsDialog] = useState(false);
  const navigate = useNavigate();
  const [itemOptions, setItemOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [error, setError] = useState("");
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filters, setFilters] = useState([{ field: "name", value: "" }]);

  const [itemsToDelete, setItemsToDelete] = useState<any[]>([]);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const offset = (currentPage - 1) * rowsPerPage;

  const [sortConfig, setSortConfig] = useState<{ orderBy: string; order: "asc" | "desc" }>({
    orderBy: "createdAt",
    order: "desc",
  });

  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (prev.orderBy === column) {
        return {
          orderBy: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          orderBy: column,
          order: "asc",
        };
      }
    });
    setCurrentPage(1); // reset ke halaman 1 saat sort berubah
  };


  // Daftar pilihan filter yang tersedia
  const availableFilters = [
    { value: "itemDetailName", label: "Item Detail Name" },
    { value: "inputAreaOfBuy", label: "Area of Buy" },
    { value: "unitPrice", label: "Unit Price" },
    { value: "supplierVendor", label: "Supplier/Vendor" },
    { value: "materialGrade", label: "Material Grade" },
    { value: "complianceStandard", label: "Detail" },
    { value: "purchaseUnitMeasurement", label: "Purchase Unit of Measurement" },
    { value: "remarks", label: "Remarks" },
  ];

  // Daftar pilihan filter yang tersedia
  const availableFiltersInit = [
    { value: "itemDetailName", label: "Item Detail Name" },
    { value: "inputAreaOfBuy", label: "Area of Buy" },
    { value: "unitPrice", label: "Unit Price" },
    { value: "supplierVendor", label: "Supplier/Vendor" },
    { value: "materialGrade", label: "Material Grade" },
    { value: "complianceStandard", label: "Detail" },
    { value: "purchaseUnitMeasurement", label: "Purchase Unit of Measurement" },
    { value: "remarks", label: "Remarks" },
  ];


  const [filterParams, setFilterParams] = useState<{
    keyword?: string;
    itemId?: string;
    itemDetailName?: string;
    inputAreaOfBuy?: string;
    unitPrice?: string;
    supplierVendor?: string;
    materialGrade?: string;
    complianceStandard?: string;
    purchaseUnitMeasurement?: string;
    remarks?: string;
  }>({});

  const fetchItemDetails = async () => {
    setIsLoading(true);
    try {
      const offset = (currentPage - 1) * rowsPerPage;

      const result = await getItemDetails({
        ...filterParams,
        limit: rowsPerPage,
        offset,
        orderBy: sortConfig.orderBy,
        order: sortConfig.order,
      });

      setRows(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchItemList = async () => {
    try {
      const result = await getItems();
      setItemOptions(result); // Simpan data item dari backend
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  };

  // Fetch data on mount
  useEffect(() => {

    fetchItemDetails();
    fetchItemList();
  }, [currentPage, rowsPerPage, filterParams, sortConfig]);




  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDeleteRows = async () => {
    try {
      await Promise.all(selectedRows.map(async (id) => await deleteItemDetail(id)));

      Swal.fire({
        icon: "success",
        title: "Item berhasil dihapus",
        confirmButtonColor: "#0F62FE",
      });

      setSelectedRows([]);
      fetchItemDetails(); // refresh data
      setOpenDeleteDialog(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal menghapus item",
        text: error.response?.data?.message || "Terjadi kesalahan",
      });
    }
  };

  // const totalPages = Math.ceil(rows.length / rowsPerPage);
  const displayedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  //details form
  const [formData, setFormData] = useState({
    itemGroup: "",
    materialGrade: "",
    complianceStandard: "",
    inputAreaOfBuy: "",
    unitPrice: "",
    supplierVendor: "",
    unitMeasurement: [{ unit: "", size: "" }],
    remarks: "",
  });

  const [categoryData, setCategoryData] = useState<{
    itemGroupName: string;
    itemDescription: string;
    selectedItem: string;
    file: File | null;
  }>({
    itemGroupName: "",
    itemDescription: "",
    selectedItem: "",
    file: null,
  });


  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (file: File) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Hanya file .png, .jpg, dan .jpeg yang diperbolehkan",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran file terlalu besar",
        text: "Ukuran maksimum adalah 5MB",
      });
      return;
    }

    setCategoryData({ ...categoryData, file });
  };

  const unitMeasurementOptions = ["Dimensions", "Weight", "Volume", "Diameter", "Thickness", "Watt", "Power Output"];
  const purchaseUnitMeasurementOptions = [
    "m3", "kilo", "buah", "m2", "zak", "pak", "lembar", "m`", "box", "titik", "batang", "dos", "rol", "cm", "ls", "pcs",
    "ons", "liter", "5 kg", "2.5 kg", "drum", "set", "OH", "unit", "ton", "/lb", "ikat", "tube", "karung", "bln"
  ];


  const [itemDetailsData, setItemDetailsData] = useState<{
    itemId: string,
    itemDetailName: string;
    materialGrade: string,
    complianceStandard: string,
    inputAreaOfBuy: string,
    unitPrice: number|null,
    supplierVendor: string,
    unitMeasurement: any[],
    remarks: string,
    initialDataProcess: string,
    purchaseUnitMeasurement: string,
    file: File | null;
  }>({
    itemId: "",
    itemDetailName: "",
    materialGrade: "",
    complianceStandard: "",
    inputAreaOfBuy: "",
    unitPrice: 0,
    supplierVendor: "",
    unitMeasurement: [{ unit: "", size: "" }],
    remarks: "",
    initialDataProcess: "",
    purchaseUnitMeasurement: "",
    file: null,
  });

  const initialItemDetailsData = {
    itemId: "",
    itemDetailName: "",
    materialGrade: "",
    complianceStandard: "",
    inputAreaOfBuy: "",
    unitPrice: 0,
    supplierVendor: "",
    unitMeasurement: [{ unit: "", size: "" }],
    remarks: "",
    initialDataProcess: "",
    purchaseUnitMeasurement: "",
    file: null,
  };

  const resetItemDetailsForm = () => {
    setItemDetailsData(initialItemDetailsData);
  };


  const handleFileDetailUpload = (file: File) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Hanya file .png, .jpg, dan .jpeg yang diperbolehkan",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran file terlalu besar",
        text: "Ukuran maksimum adalah 5MB",
      });
      return;
    }

    setItemDetailsData({ ...itemDetailsData, file });
  };

  const [addDetailErrors, setAddDetailErrors] = useState({
    itemId: false,
    unitPrice: false,
    supplierVendor: false,
    itemDetailName: false,
    // unitMeasurement: false,
    purchaseUnitMeasurement: false,
  });

  const validateAddForm = () => {
    let newErrors = {
      itemId: !itemDetailsData.itemId,
      unitPrice: !itemDetailsData.unitPrice,
      supplierVendor: !itemDetailsData.supplierVendor,
      itemDetailName: !itemDetailsData.itemDetailName,
      // unitMeasurement: !itemDetailsData.unitMeasurement,
      purchaseUnitMeasurement: !itemDetailsData.purchaseUnitMeasurement,
    };

    setAddDetailErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(e, name, value);
    setItemDetailsData({ ...itemDetailsData, [name]: value });
  };

  // ============================== EDIT DETAILS ===============================================

  const [openEditDetailDialog, setOpenEditDetailDialog] = useState(false);
  // const [editDetailData, setEditDetailData] = useState<any>(null);
  const [editDetailData, setEditDetailData] = useState<{
    itemId: string,
    itemDetailName: string,
    materialGrade: string,
    complianceStandard: string,
    inputAreaOfBuy: string,
    unitPrice: number|null,
    supplierVendor: string,
    unitMeasurement: any[],
    remarks: string,
    initialDataProcess: string,
    purchaseUnitMeasurement: string,
    file: File | null;
    photoDetail: string;
    previewUrl: string;
  }>({
    itemId: "",
    itemDetailName: "",
    materialGrade: "",
    complianceStandard: "",
    inputAreaOfBuy: "",
    unitPrice: 0,
    supplierVendor: "",
    unitMeasurement: [{ unit: "", size: "" }],
    remarks: "",
    initialDataProcess: "",
    purchaseUnitMeasurement: "",
    file: null,
    photoDetail: "",
    previewUrl: "",
  });

  const [isEditingDetail, setIsEditingDetail] = useState(false);

  const handleOpenEditDetails = async (detail) => {
    const photoDetail = await detail.photoDetail;
    await console.log(photoDetail);
    let previewUrl;
    if (photoDetail && typeof photoDetail === "string") {
      previewUrl = await fetchItemPhotoBlob(photoDetail);
    }

    console.log("detail: ", detail);

    setEditDetailData({
      ...detail,
      previewUrl,
      file: null,
    });

    // setOriginalDetailData(detail);
    setIsEditingDetail(false); // kunci form
    setOpenEditDetailDialog(true);
  };

  // const handleMeasurementEditChange = (index, field, value) => {
  //   const newMeasurements = [...editDetailData.unitMeasurement];
  //   newMeasurements[index][field] = value;
  //   setEditDetailData({ ...editDetailData, unitMeasurement: newMeasurements });
  // };

  const handleMeasurementEditChange = (index, field, value) => {
    setEditDetailData((prevData) => {
      const updatedMeasurements = [...(prevData.unitMeasurement || [])];

      // Pastikan index ada
      if (!updatedMeasurements[index]) {
        updatedMeasurements[index] = { unit: "", size: "" };
      }

      updatedMeasurements[index][field] = value;

      return {
        ...prevData,
        unitMeasurement: updatedMeasurements,
      };
    });
    // console.log(editDetailData)
  };


  const addMeasurementEditField = () => {
    setEditDetailData({
      ...editDetailData,
      unitMeasurement: [...editDetailData.unitMeasurement, { unit: "", size: "" }],
    });
  };

  const deleteMeasurementEditField = (index) => {
    setEditDetailData((prevData) => {
      const updated = [...(prevData.unitMeasurement || [])];
      updated.splice(index, 1);
      return {
        ...prevData,
        unitMeasurement: updated,
      };
    });
  };


  const handleEditDetailInputChange = (e) => {
    if (!isEditingDetail) return; // Hanya bisa diedit saat mode edit aktif
    const { name, value } = e.target;
    setEditDetailData({ ...editDetailData, [name]: value });
  };

  const handleEditDetailFileUpload = (file) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Hanya file .png, .jpg, dan .jpeg yang diperbolehkan",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran file terlalu besar",
        text: "Ukuran maksimum adalah 5MB",
      });
      return;
    }

    setEditDetailData({ ...editDetailData, file });
  };

  const [editDetailErrors, setEditDetailErrors] = useState({
    itemId: false,
    unitPrice: false,
    supplierVendor: false,
    itemDetailName: false,
    // unitMeasurement: false,
    purchaseUnitMeasurement: false,
  });

  const validateEditForm = () => {
    let newErrors = {
      itemId: !editDetailData.itemId,
      unitPrice: !editDetailData.unitPrice,
      supplierVendor: !editDetailData.supplierVendor,
      itemDetailName: !editDetailData.itemDetailName,
      // unitMeasurement: !editDetailData.unitMeasurement,
      purchaseUnitMeasurement: !editDetailData.purchaseUnitMeasurement,
    };

    setEditDetailErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  const handleMeasurementChange = (index, field, value) => {
    const newMeasurements = [...itemDetailsData.unitMeasurement];
    newMeasurements[index][field] = value;
    setItemDetailsData({ ...itemDetailsData, unitMeasurement: newMeasurements });
  };

  const addMeasurementField = () => {
    setItemDetailsData({
      ...itemDetailsData,
      unitMeasurement: [...itemDetailsData.unitMeasurement, { unit: "", size: "" }],
    });
  };

  const deleteMeasurementField = (index) => {
    const updatedUnits = itemDetailsData.unitMeasurement.filter((_, i) => i !== index);
    setItemDetailsData({
      ...itemDetailsData,
      unitMeasurement: updatedUnits,
    });
  };


  const handleSubmitAddItemDetails = async () => {
    if (!validateAddForm()) {
      showPopupMandatoryError();
      return;
    }
    try {

      const newDetail = await createItemDetail(itemDetailsData);

      if (itemDetailsData.file) {
        await uploadItemDetailPhoto(newDetail.id, itemDetailsData.file);
      }

      Swal.fire({
        icon: "success",
        title: "Item Detail berhasil ditambahkan",
        confirmButtonColor: "#0F62FE",
      });
      await resetItemDetailsForm();
      await setOpenAddItemDetailsDialog(false);
      await fetchItemDetails();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal menambahkan item detail",
        text: error.response?.details || "Terjadi kesalahan",
      });
    }
  };

  const handleSubmitEditDetail = async () => {
    if (!validateEditForm()) {
      showPopupMandatoryError();
      return;
    }
    try {
      const payload = {
        ...editDetailData,
      };

      // Panggil API update item detail
      await editItemDetail(payload);

      // Jika ada gambar baru, upload
      if (editDetailData.file) {
        await uploadItemDetailPhoto(editDetailData.id, editDetailData.file);
      }

      Swal.fire({ icon: "success", title: "Berhasil memperbarui item detail" });
      setOpenEditDetailDialog(false);
      fetchItemDetails();
    } catch (error) {
      Swal.fire({ icon: "error", title: "Gagal", text: "Gagal menyimpan perubahan" });
    }
  };

  const handleCancelEditCategory = () => {
    setEditCategoryData(originalCategoryData); // Kembalikan nilai asli
    setIsEditingCategory(false); // Kunci kembali form
    setOpenEditCategoryDialog(false);
  };

  const showPopupMandatoryError = () => {
    Swal.fire({ icon: "error", title: "Data Belum Lengkap", text: "Lengkapi data yang wajib diisi." });
  }

  // FILTER

  const handleFilterChange = (index: number, key: string, value: string) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    setFilters(newFilters);
  };

  const handleAddFilter = () => {
    setFilters([...filters, { field: "nik", value: "" }]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      // Buat object parameter pencarian sesuai `ItemSearchParams`
      const searchParams: ItemDetailSearchParams = {};

      filters.forEach((filter) => {
        if (filter.value.trim() !== "") {
          searchParams[filter.field] = filter.value;
        }
      });

      // // Panggil fungsi pencarian dari itemServices.tsx
      // const response = await getItems(searchParams);
      // console.log(response);
      // // Format hasil pencarian sebelum ditampilkan di tabel
      // handleSearchResults(response.data);

      setFilterParams({ ...searchParams, itemId: selectedItem });
      setOpenFilterDialog(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal mengambil data",
        text: error.response?.data?.message || "Terjadi kesalahan",
      });
      handleCloseFilter();
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Tambahkan fungsi ini
  const handleSearchResults = (results) => {
    setRows(results); // Update rows agar sesuai dengan format tabel
    setOpenFilterDialog(false); // Tutup dialog setelah apply filter
  };

  const handleCloseFilter = () => {
    setOpenFilterDialog(false);
  };

  const handleResetFilters = () => {
    setFilters([{ field: "", value: "" }]); // Reset ke kondisi awal dengan satu filter kosong
  };

  //======================= Search ===================================
  const handleSearch = async () => {
    console.log("search", searchQuery)
    // if (searchQuery.trim() === "") return; // Hindari pencarian kosong
    setIsLoading(true);
    setError("");

    try {
      // const results = await getItems({
      //   keyword: searchQuery,
      //   limit: rowsPerPage,
      //   offset,
      // });
      setFilterParams(
        {
          keyword: searchQuery
        }
      );
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Tampilkan loading
      Swal.fire({
        title: 'Preparing Export...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Opsi 1: Export semua data (dengan filter yang sama seperti tabel)
      const exportData = await getExportData();

      // Opsi 2: Export hanya data yang terlihat di tabel saat ini
      // const exportData = rows.map(formatEmployeeForExport);

      // Opsi 3: Export hanya data yang dipilih
      // if (selectedRows.length === 0) {
      //   Swal.fire({
      //     icon: 'warning',
      //     title: 'No Selection',
      //     text: 'Please select at least one employee to export',
      //   });
      //   return;
      // }
      // const exportData = rows
      //   .filter(row => selectedRows.includes(row.id))
      //   .map(formatEmployeeForExport);

      // Buat worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Buat workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ItemDetails");

      // Generate file Excel
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `ItemDetails_${date}.xlsx`);

      // Tutup loading
      Swal.close();

    } catch (error) {
      console.error('Export failed:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.message || 'Failed to export employee data',
      });
    }
  };

  // Fungsi untuk mengambil data yang akan diexport
  const getExportData = async () => {
    const allItems = await getItemDetails({
      ...filterParams,
      limit: 10000, // Ambil semua data
      offset: 0,
      orderBy: sortConfig.orderBy,
      order: sortConfig.order,
    });

    return allItems.data.map(detail => ({
      'Item Category': itemOptions.find(item => item.id === detail.itemId)?.name || "-",
      'Item Detail Name': detail.itemDetailName,
      'Area of Buy': detail.inputAreaOfBuy,
      'Unit Price': detail.unitPrice,
      'Supplier/Vendor': detail.supplierVendor,
      'Material Grade': detail.materialGrade,
      'Detail': detail.complianceStandard,
      'Purchase Unit of Measurement': detail.purchaseUnitMeasurement,
      'Remarks': detail.remarks,
      'Created At': detail.createdAt,
    }));
  };


  return (
    <Box sx={{ padding: 3 }}>
      {/* Header & Tabs */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Item Details List
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#D1F7D1", color: "#000", textTransform: "none" }}
            onClick={() => navigate("/data-management/item")}
          >
            Item
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#F0F3FA", color: "#000", textTransform: "none" }}
            onClick={() => navigate("/data-management/item-detail")}
          >
            Item Details
          </Button>
        </Box>

      </Box>

      {/* Toolbar */}
      <Box className="toolbar">
        {/* Left Controls */}
        <Box className="leftControls">
          <Typography>Show</Typography>
          <Select value={rowsPerPage} onChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setCurrentPage(1); // reset ke halaman 1 saat ganti limit
          }} size="small">
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
          </Select>


          {/* Search Button & Input */}
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => setShowSearch((prev) => !prev)}
            className="searchButton"
          >
            Search
          </Button>
          {showSearch && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={async (e) => {
                await setSearchQuery(e.target.value);
              }}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()} // Pencarian dengan Enter
              autoFocus
              className="searchInput"
            />
          )}

          {/* Menampilkan Error */}
          {error && <p style={{ color: "red" }}>{error}</p>}
        </Box>

        {/* Right Controls */}
        <Box className="rightControls">
          <Button
            startIcon={<Delete />}
            variant="text"
            onClick={() => {
              const selectedData = rows.filter((row) => selectedRows.includes(row.id));
              setItemsToDelete(selectedData);
              setOpenDeleteDialog(true);
            }}
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
          {/* Filter Button */}
          <Button
            startIcon={<FilterList />}
            variant="text"
            onClick={() => setOpenFilterDialog(true)}
          >
            Filters
          </Button>
          <Button startIcon={<Download />} variant="contained" className="exportButton" onClick={() => handleExport()}>
            Export
          </Button>
          <Button startIcon={<Add />} variant="contained" className="addItemDetailsButton" onClick={() => setOpenAddItemDetailsDialog(true)}>
            Add New Item Details
          </Button>
        </Box>
      </Box>


      {/* Delete Button & Subtitle */}
      {/* <Box className="deleteContainer">
        <IconButton
          onClick={() => setOpenDeleteDialog(true)}
          disabled={selectedRows.length === 0} // Disable jika tidak ada baris yang dipilih
          className="deleteButton"
        >
          <TrashOutline color={"#333"} height="20px" width="20px" />
        </IconButton>
      </Box> */}

      <Box className="deleteContainer">
        <Typography variant="body2" className="detailsSubtitle">
          Details List Of All Item Details
        </Typography>
      </Box>


      {/* Table */}
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHead">
            <TableRow>
              <TableCell padding="checkbox" className="tableCell">
                <Checkbox
                  indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                  checked={selectedRows.length === rows.length}
                  onChange={() =>
                    setSelectedRows(selectedRows.length === rows.length ? [] : rows.map((row) => row.id))
                  }
                />
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell className="tableCell">
                <strong>Item Category</strong>
                {/* {sortConfig.orderBy === "itemCategory" && (sortConfig.order === "asc" ? " ▲" : " ▼")} */}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("itemDetailName")} style={{ cursor: "pointer" }}>
                <strong>Item Detail Name</strong>
                {sortConfig.orderBy === "itemDetailName" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("inputAreaOfBuy")} style={{ cursor: "pointer" }}>
                <strong>Area of Buy</strong>
                {sortConfig.orderBy === "inputAreaOfBuy" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("unitPrice")} style={{ cursor: "pointer" }}>
                <strong>Unit Price</strong>
                {sortConfig.orderBy === "unitPrice" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("supplierVendor")} style={{ cursor: "pointer" }}>
                <strong>Supplier/Vendor</strong>
                {sortConfig.orderBy === "supplierVendor" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("materialGrade")} style={{ cursor: "pointer" }}>
                <strong>Material Grade</strong>
                {sortConfig.orderBy === "materialGrade" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("complianceStandard")} style={{ cursor: "pointer" }}>
                <strong>Detail</strong>
                {sortConfig.orderBy === "complianceStandard" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("purchaseUnitMeasurement")} style={{ cursor: "pointer" }}>
                <strong>Purchase Unit of Measurement</strong>
                {sortConfig.orderBy === "purchaseUnitMeasurement" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("remarks")} style={{ cursor: "pointer" }}>
                <strong>Remarks</strong>
                {sortConfig.orderBy === "remarks" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell" onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                <strong>Created At</strong>
                {sortConfig.orderBy === "createdAt" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id} selected={selectedRows.includes(row.id)}>
                <TableCell padding="checkbox" className="tableCell">
                  <Checkbox
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                  />
                </TableCell>
                <TableCell>
                  {/* Nomor urut disesuaikan dengan pagination */}
                  {offset + index + 1}
                </TableCell>
                <TableCell>{itemOptions.find(item => item.id === row.itemId)?.name || "-"}</TableCell>
                <TableCell className="tableCell" title={row.itemDetailName}>{row.itemDetailName}</TableCell>
                <TableCell className="tableCell" title={row.inputAreaOfBuy}>{row.inputAreaOfBuy}</TableCell>
                <TableCell className="tableCell" title={row.unitPrice}>{row.unitPrice}</TableCell>
                <TableCell className="tableCell" title={row.supplierVendor}>{row.supplierVendor}</TableCell>
                <TableCell className="tableCell" title={row.materialGrade}>{row.materialGrade}</TableCell>
                <TableCell className="tableCell" title={row.complianceStandard}>{row.complianceStandard}</TableCell>
                <TableCell className="tableCell" title={row.purchaseUnitMeasurement}>{row.purchaseUnitMeasurement}</TableCell>
                <TableCell className="tableCell" title={row.remarks}>{row.remarks}</TableCell>
                <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenEditDetails(row)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        className="paginationContainer"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "16px 0",
        }}
      >
        {/* Info jumlah data */}
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Showing ${offset + 1} to ${Math.min(
            offset + rowsPerPage,
            totalItems
          )} of ${totalItems} entries`}
        </Typography>

        {/* Komponen Pagination */}
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, page) => setCurrentPage(page)}
          className="pagination"
          showFirstButton
          showLastButton
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={{ first: FirstPage, last: LastPage }}
            />
          )}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Rows?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the selected rows?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteRows} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Add Item Details */}
      <Dialog open={openAddItemDetailsDialog} onClose={() => setOpenAddItemDetailsDialog(false)} fullWidth maxWidth="lg"
        sx={{ backdropFilter: "blur(2px)", borderRadius: "10px", border: "2px solid #EAECF0" }}>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">Add New Item Details</Typography>
            <IconButton onClick={() => setOpenAddItemDetailsDialog(false)} color="error"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Grid Layout */}
            <Grid container spacing={3}>
              {/* File Upload Section */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    height: "300px",
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    handleFileDetailUpload(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {itemDetailsData.file ? (
                    <img
                      src={URL.createObjectURL(itemDetailsData.file)}
                      alt="Preview"
                      style={{ width: "100%", height: "auto", borderRadius: "8px", objectFit: "contain", maxHeight: 180 }}
                    />
                  ) : (
                    <>
                      <PhotoCamera fontSize="large" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload image
                      </Typography>
                    </>
                  )}
                  <input
                    hidden
                    accept="image/png, image/jpeg, image/jpg"
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileDetailUpload(e.target.files[0])}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (.png, .jpg, .jpeg)
                  </Typography>
                </Box>
              </Grid>

              {/* Left Section */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={itemOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={itemOptions.find(opt => opt.id === itemDetailsData.itemId) || null}
                  onChange={(event, newValue) => {
                    handleChange({
                      target: {
                        name: "itemId",
                        value: newValue ? newValue.id : ""
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item Category"
                      placeholder="Select Item"
                      margin="dense"
                      fullWidth
                      error={!itemDetailsData.itemId}
                      helperText={!itemDetailsData.itemId ? "Mandatory Field" : ""}
                    />
                  )}
                />

                <TextField
                  label="Input Item Detail Name"
                  name="itemDetailName"
                  value={itemDetailsData.itemDetailName}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  error={!itemDetailsData.itemDetailName}
                  helperText={!itemDetailsData.itemDetailName ? "Mandatory Field" : ""}
                />

                <TextField
                  label="Input Area Of Buy"
                  name="inputAreaOfBuy"
                  value={itemDetailsData.inputAreaOfBuy}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />

                <TextField
                  label="Input Unit Price (IDR)"
                  name="unitPrice"
                  value={itemDetailsData.unitPrice}
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  margin="dense"
                  error={!itemDetailsData.unitPrice}
                  helperText={!itemDetailsData.unitPrice ? "Mandatory Field" : ""}
                />

                <TextField
                  label="Input Supplier/Vendor"
                  name="supplierVendor"
                  value={itemDetailsData.supplierVendor}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  error={!itemDetailsData.supplierVendor}
                  helperText={!itemDetailsData.supplierVendor ? "Mandatory Field" : ""}
                />
              </Grid>

              {/* Right Section */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Input Material Grade (if any)"
                  name="materialGrade"
                  value={itemDetailsData.materialGrade}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />

                <TextField
                  label="Input Compliance Standard"
                  name="complianceStandard"
                  value={itemDetailsData.complianceStandard}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                // error={!itemDetailsData.materialGrade && !itemDetailsData.complianceStandard}
                // helperText={!itemDetailsData.materialGrade && !itemDetailsData.complianceStandard ? "Mandatory If Material Grade Exist" : ""}
                />

                {/* Measurement Units */}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Input Unit Pengukuran</Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Typography>Satuan</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>Unit</Typography>
                  </Grid>
                  <Grid item xs={2}></Grid>
                </Grid>

                {itemDetailsData.unitMeasurement.map((val, index) => (
                  <Grid container spacing={1} alignItems="center" key={index} sx={{ mb: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        placeholder="Panjang"
                        value={val.unit}
                        onChange={(e) => handleMeasurementChange(index, "unit", e.target.value)}
                        fullWidth
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        placeholder="10 M"
                        value={val.size}
                        onChange={(e) => handleMeasurementChange(index, "size", e.target.value)}
                        fullWidth
                        margin="dense"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      {index === itemDetailsData.unitMeasurement.length - 1 ? (
                        <IconButton onClick={() => addMeasurementField()} color="primary">
                          <Add />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => deleteMeasurementField(index)} color="error">
                          <DeleteOutline />
                        </IconButton>
                      )
                      }

                    </Grid>
                  </Grid>
                ))}

                {itemDetailsData.unitMeasurement.length === 0 && (
                  <Typography color="error">Mandatory Field</Typography>
                )}

                <TextField
                  select
                  label="Purchase Unit Of Measurement"
                  name="purchaseUnitMeasurement"
                  value={itemDetailsData.purchaseUnitMeasurement}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  error={!itemDetailsData.purchaseUnitMeasurement}
                  helperText={!itemDetailsData.purchaseUnitMeasurement ? "Mandatory Field" : ""}
                >
                  {purchaseUnitMeasurementOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Remarks Section */}
                <Typography variant="subtitle2" sx={{ mt: 3 }}>Add Remarks</Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>Sample Description</Typography>
                <TextField
                  name="remarks"
                  value={itemDetailsData.remarks}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  margin="dense"
                  placeholder="Placeholder text"
                />
                <Typography variant="caption" sx={{ textAlign: "right", display: "block", color: "#666" }}>
                  {itemDetailsData.remarks.length}/160 Characters
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setOpenAddItemDetailsDialog(false)} sx={{ border: "1px solid #94989E", color: "#13161A", textTransform: "uppercase", letterSpacing: "1.6px" }}>Cancel</Button>
          <Button onClick={handleSubmitAddItemDetails} variant="contained" sx={{ backgroundColor: "#14BA6D", color: "white", textTransform: "uppercase", letterSpacing: "1.6px" }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Edit Item Details */}
      <Dialog open={openEditDetailDialog} onClose={() => setOpenEditDetailDialog(false)} fullWidth maxWidth="lg"
        sx={{ backdropFilter: "blur(2px)", borderRadius: "10px", border: "2px solid #EAECF0" }}>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">Edit New Item Details</Typography>
            <IconButton onClick={() => setOpenEditDetailDialog(false)} color="error"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Grid Layout */}
            <Grid container spacing={3}>
              {/* File Upload Section */}
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative"
                  }}
                  disabled={!isEditingDetail}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    if (isEditingDetail) {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      handleEditDetailFileUpload(file);
                    }
                  }}
                  onClick={() => {
                    if (isEditingDetail) {
                      editFileInputRef.current?.click()
                    }
                  }}
                >
                  {editDetailData.file ? (
                    // Jika user baru upload gambar
                    <img
                      src={URL.createObjectURL(editDetailData.file)}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        objectFit: "contain",
                        maxHeight: 180,
                      }}
                    />
                  ) : editDetailData.previewUrl ? (
                    // Jika sudah ada foto dari server
                    <img
                      src={editDetailData.previewUrl}
                      alt="Existing"
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        objectFit: "contain",
                        maxHeight: 180,
                      }}
                    />
                  ) : (
                    // Jika tidak ada gambar sama sekali
                    <>
                      <PhotoCamera fontSize="large" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload image
                      </Typography>
                    </>
                  )}

                  <input
                    hidden
                    accept="image/png, image/jpeg, image/jpg"
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => handleEditDetailFileUpload(e.target.files[0])}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (.png, .jpg, .jpeg)
                  </Typography>
                </Box>
              </Grid>

              {/* Left Section */}
              <Grid item xs={12} md={4}>

                <Autocomplete
                  options={itemOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={itemOptions.find(opt => opt.id === editDetailData.itemId) || null}
                  onChange={(event, newValue) => {
                    handleEditDetailInputChange({
                      target: {
                        name: "itemId",
                        value: newValue ? newValue.id : ""
                      }
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item Category"
                      placeholder="Select Item"
                      margin="dense"
                      fullWidth
                      error={!editDetailData.itemId}
                      helperText={!editDetailData.itemId ? "Mandatory Field" : ""}
                      disabled={!isEditingDetail}
                    />
                  )}
                />

                <TextField
                  label="Input Item Detail Name"
                  name="itemDetailName"
                  value={editDetailData.itemDetailName}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  error={!editDetailData.itemDetailName}
                  helperText={!editDetailData.itemDetailName ? "Mandatory Field" : ""}
                  disabled={!isEditingDetail}
                />

                <TextField
                  label="Input Area Of Buy"
                  name="inputAreaOfBuy"
                  value={editDetailData.inputAreaOfBuy}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  disabled={!isEditingDetail}
                />

                <TextField
                  label="Input Unit Price (IDR)"
                  name="unitPrice"
                  value={editDetailData.unitPrice}
                  onChange={handleEditDetailInputChange}
                  type="number"
                  fullWidth
                  margin="dense"
                  error={!editDetailData.unitPrice}
                  helperText={!editDetailData.unitPrice ? "Mandatory Field" : ""}
                  disabled={!isEditingDetail}
                />

                <TextField
                  label="Input Supplier/Vendor"
                  name="supplierVendor"
                  value={editDetailData.supplierVendor}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  disabled={!isEditingDetail}
                />
              </Grid>

              {/* Right Section */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Input Material Grade (if any)"
                  name="materialGrade"
                  value={editDetailData.materialGrade}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  disabled={!isEditingDetail}
                />

                <TextField
                  label="Input Compliance Standard"
                  name="complianceStandard"
                  value={editDetailData.complianceStandard}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  // error={!editDetailData.materialGrade && !editDetailData.complianceStandard}
                  // helperText={!editDetailData.materialGrade && !editDetailData.complianceStandard ? "Mandatory If Material Grade Exist" : ""}
                  disabled={!isEditingDetail}
                />

                {/* Measurement Units */}
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Input Unit Pengukuran</Typography>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Typography>Satuan</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>Unit</Typography>
                  </Grid>
                  <Grid item xs={2}></Grid>
                </Grid>

                {editDetailData.unitMeasurement.map((val, index) => {
                  const usedFields = editDetailData.unitMeasurement
                    .map((item, i) => i !== index && item.unit) // ambil semua unit yang dipakai kecuali yang sedang diedit
                    .filter(Boolean); // hilangkan undefined/null

                  const availableOptions = unitMeasurementOptions.filter(
                    (opt) => !usedFields.includes(opt) || opt === val.unit // tetap munculkan opsi yang sedang dipilih saat ini
                  );

                  return (
                    <Grid container spacing={1} alignItems="center" key={index} sx={{ mb: 1 }}>
                      {/* <Grid item xs={6}>
                        <TextField
                          select
                          value={val.unit}
                          onChange={(e) => handleMeasurementEditChange(index, "unit", e.target.value)}
                          fullWidth
                          margin="dense"
                          disabled={!isEditingDetail}
                        >
                          {availableOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid> */}
                      <Grid item xs={6}>
                        <TextField
                          placeholder="Panjang"
                          value={val.unit}
                          onChange={(e) => handleMeasurementEditChange(index, "unit", e.target.value)}
                          fullWidth
                          margin="dense"
                          disabled={!isEditingDetail}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          placeholder="10 M"
                          value={val.size}
                          onChange={(e) => handleMeasurementEditChange(index, "size", e.target.value)}
                          fullWidth
                          margin="dense"
                          disabled={!isEditingDetail}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        {index === editDetailData.unitMeasurement.length - 1 ? (
                          <IconButton onClick={() => addMeasurementEditField()} color="primary"
                            disabled={!isEditingDetail}>
                            <Add />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => deleteMeasurementEditField(index)} color="error"
                            disabled={!isEditingDetail}>
                            <DeleteOutline />
                          </IconButton>
                        )
                        }

                      </Grid>
                    </Grid>
                  )
                })}

                {editDetailData.unitMeasurement.length === 0 && (
                  <Typography color="error">Mandatory Field</Typography>
                )}

                <TextField
                  select
                  label="Purchase Unit Of Measurement"
                  name="purchaseUnitMeasurement"
                  value={editDetailData.purchaseUnitMeasurement}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  margin="dense"
                  error={!editDetailData.purchaseUnitMeasurement}
                  helperText={!editDetailData.purchaseUnitMeasurement ? "Mandatory Field" : ""}
                  disabled={!isEditingDetail}
                >
                  {purchaseUnitMeasurementOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Remarks Section */}
                <Typography variant="subtitle2" sx={{ mt: 3 }}>Add Remarks</Typography>
                <Typography variant="caption" sx={{ color: "#666" }}>Sample Description</Typography>
                <TextField
                  name="remarks"
                  value={editDetailData.remarks}
                  onChange={handleEditDetailInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  margin="dense"
                  placeholder="Placeholder text"
                  disabled={!isEditingDetail}
                />
                <Typography variant="caption" sx={{ textAlign: "right", display: "block", color: "#666" }}>
                  {editDetailData.remarks.length}/160 Characters
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        {/* Dialog Actions */}
        <DialogActions sx={{ justifyContent: "space-between", padding: 2 }}>
          {!isEditingDetail ? (
            // Tombol Edit Muncul di Awal
            <Button onClick={() => setIsEditingDetail(true)} variant="contained" color="primary" sx={{ width: 120 }}>
              EDIT
            </Button>
          ) : (
            // Tombol Save & Cancel Muncul Saat Edit
            <>
              <Button onClick={() => setIsEditingDetail(false)} variant="outlined" color="error" sx={{ width: 120 }}>
                CANCEL
              </Button>
              <Button variant="contained" color="success" sx={{ width: 120 }} onClick={handleSubmitEditDetail}>
                SAVE
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ======================= Filter Dialog ============================= */}
      <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)}>
        <DialogTitle>Advanced Search</DialogTitle>
        <DialogContent>
          <Box key={'itemId'} display="flex" alignItems="center" gap={1} mb={2}>
            <TextField
              select
              label="Filter"
              variant="outlined"
              size="small"
              value="itemId"
              sx={{ mt: 1, minWidth: 160 }}
              disabled
            >

              <MenuItem selected key="itemId" value="itemId">
                Item Category
              </MenuItem>
            </TextField>

            <Autocomplete
              fullWidth
              options={itemOptions}
              getOptionLabel={(option) => option.name || ""}
              value={itemOptions.find(opt => opt.id === selectedItem) || null}
              onChange={(event, newValue) => {
                setSelectedItem(newValue?.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item Category"
                  placeholder="Select Item"
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </Box>
          {filters.map((filter, index) => {
            const usedFields = filters.map((f, i) => i !== index && f.field).filter(Boolean);
            const availableOptions = availableFilters.filter((opt) => !usedFields.includes(opt.value));

            return (
              <Box key={index} display="flex" alignItems="center" gap={1} mb={2}>
                <TextField
                  select
                  value={filter.field}
                  onChange={(e) => handleFilterChange(index, "field", e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, minWidth: 160 }}
                >
                  {availableOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Value"
                  variant="outlined"
                  size="small"
                  value={filter.value}
                  onChange={(e) => handleFilterChange(index, "value", e.target.value)}
                  sx={{ mt: 1 }}
                />

                {index > 0 && (
                  <IconButton onClick={() => handleRemoveFilter(index)}>
                    <Remove />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {filters.length < availableFilters.length && (
            <Button startIcon={<Add />} onClick={handleAddFilter}>
              Add Filter
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetFilters}>Reset Filter</Button>
          <Button onClick={handleCloseFilter}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyFilters} disabled={isLoading}>
            {isLoading ? "Loading..." : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Rows?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the selected item(s)?
          </DialogContentText>
          {/* <ul style={{ paddingLeft: "1.5rem", marginTop: "0.5rem" }}>
            {itemsToDelete.map((item) => (
              <li key={item.id}>
                <strong>{item.name}</strong> (Code: {item.code})
              </li>
            ))}
          </ul> */}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteRows} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ItemDetails;
